import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Share, Plus, ChevronUp, Calendar } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isMobile = () => isIOS() || isAndroid() || (window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const SESSION_SHOWN_KEY = 'k9os_install_shown_this_session';

// True when we've already shown the popup in the current browser session.
// Uses sessionStorage (not localStorage) so it resets when the tab closes,
// but persists across in-tab navigations so visitors aren't badgered by
// the same popup on every page click within a single session.
const shownThisSession = () => {
  try { return sessionStorage.getItem(SESSION_SHOWN_KEY) === '1'; }
  catch { return false; }
};
const markShownThisSession = () => {
  try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch { /* storage blocked */ }
};

export default function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    // 1) Never show if the app is already running standalone (installed).
    if (isStandalone()) return;

    // 2) Soft session cap — if we already fired the popup once in this
    //    session (any page/route), don't re-fire on subsequent page
    //    loads. Closing the tab clears sessionStorage so the popup
    //    re-appears on the next visit. This replaces the every-load
    //    behaviour without going back to the 12h localStorage cooldown.
    if (shownThisSession()) return;

    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); setOpen(true); markShownThisSession(); };
    const onAppInstalled = () => setOpen(false);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    if (isMobile()) setTimeout(() => { setOpen(true); markShownThisSession(); }, 1500);
    else setTimeout(() => { setOpen(true); markShownThisSession(); }, 8000);
    window.__jfk9ShowInstall = () => setOpen(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      setOpen(false);
      window.dispatchEvent(new Event("jfk9:install-dismissed"));
    }
  };

  const dismiss = () => {
    // No cooldown persisted — just close for this render; next page load
    // re-opens the popup per user request.
    setOpen(false);
    window.dispatchEvent(new Event("jfk9:install-dismissed"));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] pointer-events-none sm:inset-x-auto sm:right-4 sm:bottom-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }} data-testid="install-prompt">
      <div className="max-w-sm mx-auto pointer-events-auto px-3 pb-3 sm:px-0 sm:pb-0 jfk-paw-slide-up relative">
        {/* Dog photo — sits BEHIND the popup card (z-0, card is z-10) so
            the paws rest on the top edge of the card like the dog is
            leaning on it. `bottom: 100%` anchors the dog's paws exactly
            at the card's top edge so nothing overlaps the card fill.
            `pointer-events-none` keeps the CTA tappable. */}
        <div
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-0"
          style={{ bottom: 'calc(100% - 40px)' }}
          aria-hidden="true"
        >
          <div className="jfk-dog-peek">
            <img
              src="/img/k9os-install-mal.png"
              alt=""
              className="h-[130px] sm:h-[170px] w-auto object-contain block drop-shadow-[0_10px_20px_rgba(0,0,0,0.35)]"
              loading="lazy"
            />
          </div>
        </div>

        <div className="relative z-10 bg-white dark:bg-slate-900 border-2 border-[#1B3A87] dark:border-[#93C5FD] rounded-2xl shadow-2xl overflow-hidden">

          {/* Close button — top-right, high-visibility black pill so it's
              impossible to miss. z-30 to stay above both the card content
              and the dog's paws that may overlap the top edge. */}
          <button
            onClick={dismiss}
            className="absolute top-1.5 right-1.5 z-30 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black hover:bg-slate-800 text-white shadow-md border border-white/20"
            data-testid="install-dismiss"
            aria-label="Dismiss install prompt"
          >
            <X className="w-4 h-4" strokeWidth={2.75} />
          </button>

          <div className="pt-3.5 pb-3 px-4">
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#C8102E]">Quick Access</div>
            <div className="font-display font-bold text-[#0B1220] dark:text-slate-50 text-[15px] leading-tight mt-0.5">
              Save K9os App to your Home Screen
            </div>
            <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 leading-snug">
              One tap to book boarding, message your trainer, and track your dog&apos;s progress.
            </p>

            {/* Huge Schedule-a-Free-Demo CTA — takes the visitor straight to
                the /contact demo lead form. Sits above the install steps so
                it's the first thing the eye lands on when the popup opens. */}
            <Link
              to='/contact'
              onClick={dismiss}
              className='mt-3 flex items-center justify-center gap-2 w-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-display font-bold text-[15px] py-3 rounded-xl shadow-lg hover:-translate-y-0.5 transition-all uppercase tracking-wide'
              data-testid='install-schedule-demo'
            >
              <Calendar className='w-4 h-4' /> Schedule a Free Demo
            </Link>
            <div className='mt-3 mb-2 text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400 text-center'>
              &mdash; or add K9os to your phone &mdash;
            </div>

            {isIOS() ? (
              <div className="mt-2.5 space-y-1 text-[11px] text-slate-700 dark:text-slate-200">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">1.</span>
                  Tap the <Share className="w-3 h-3 inline mx-0.5" /> <strong>Share</strong> button at the bottom of Safari.
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">2.</span>
                  Scroll down and tap <Plus className="w-3 h-3 inline mx-0.5" /> <strong>Add to Home Screen</strong>.
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">3.</span>
                  Tap <strong>Add</strong> in the top-right.
                </div>
              </div>
            ) : isAndroid() && deferred ? (
              <button
                onClick={install}
                className="mt-2.5 w-full bg-[#C8102E] hover:bg-[#A50D26] text-white py-2 rounded-lg font-semibold text-[13px]"
                data-testid="install-confirm"
              >
                <ChevronUp className="w-3.5 h-3.5 inline mr-1" /> Install K9os App
              </button>
            ) : (
              <div className="mt-2.5 space-y-1 text-[11px] text-slate-700 dark:text-slate-200">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">1.</span>
                  Open your browser menu (⋮ on Android, Share on iOS).
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">2.</span>
                  Tap <strong>Add to Home Screen</strong> or <strong>Install</strong>.
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1B3A87] dark:text-[#93C5FD]">3.</span>
                  Confirm and you&apos;ll find K9os on your home screen.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
